import React, { ReactNode, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useGetMonitoringCounts } from '@/hooks/useGetMonitoringStats';
import { useGetSubscription } from '@/hooks/useSubscription';
import { CustomerData } from '@/sections/customerManagement/types';
import { Monitoring } from '@/sections/settings/monitoring';
import { ConfigureSubscription } from '@/sections/settings/subscription/configure';
import { SubscriptionSummary } from '@/sections/settings/subscription/summary';
import { SubscriptionTiers } from '@/sections/settings/subscription/tier';
import { useAuth } from '@/state/auth';

interface CustomerSubscriptionTabProps {
  customer: CustomerData;
}

export const CustomerSubscriptionTab: React.FC<
  CustomerSubscriptionTabProps
> = ({ customer }) => {
  const { isPraetorianUser } = useAuth();
  const [configureSubscriptionModalOpen, setConfigureSubscriptionModalOpen] =
    useState(false);

  // Fetch customer-specific subscription data
  const { data: subscriptionValue, status: subscriptionStatus } =
    useGetSubscription({ email: customer.email });

  // Fetch monitoring counts for subscription summary display
  const { data: monitoringCounts, status: monitoringCountsStatus } =
    useGetMonitoringCounts({ email: customer.email });

  if (
    subscriptionStatus === 'pending' ||
    monitoringCountsStatus === 'pending'
  ) {
    return <Spinner centered size="xl" />;
  }

  return (
    <>
      <div className="relative flex h-full flex-col gap-6">
        {subscriptionValue && (
          <>
            {isPraetorianUser && (
              <Button
                className="absolute top-0 right-0"
                size="sm"
                onClick={() => {
                  setConfigureSubscriptionModalOpen(true);
                }}
              >
                Update Subscription
              </Button>
            )}
            <CategoryContainer title="Current Subscription">
              <SubscriptionSummary
                {...subscriptionValue}
                pricingCurrency="USD"
                billingCycle="annual"
                billingPaymentMethod="Invoice"
                monitoringCounts={monitoringCounts}
              />
            </CategoryContainer>
            <CategoryContainer title="Available Subscription Tiers">
              <SubscriptionTiers
                selectedTier={subscriptionValue.selectedTier}
              />
            </CategoryContainer>
            <CategoryContainer title="Asset Monitoring">
              <Monitoring email={customer.email} />
            </CategoryContainer>
          </>
        )}
        {!subscriptionValue && isPraetorianUser && (
          <div className="flex h-full items-center justify-center">
            <Button
              onClick={() => {
                setConfigureSubscriptionModalOpen(true);
              }}
            >
              Configure Subscription
            </Button>
          </div>
        )}
        <ConfigureSubscription
          open={configureSubscriptionModalOpen}
          onClose={() => setConfigureSubscriptionModalOpen(false)}
          customerEmail={customer.email}
        />
      </div>
    </>
  );
};

function CategoryContainer(props: { title: string; children: ReactNode }) {
  return (
    <div className="">
      <h2 className="text-chariot-brand-primary mb-6 text-2xl font-bold">
        {props.title}
      </h2>
      {props.children}
    </div>
  );
}
